import React, { useState } from "react";
import { Typography, Box, useTheme, Divider, MenuItem, Select, FormControl, InputLabel, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Formik } from "formik";
import * as yup from "yup";
import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAlert } from "../../../global/AlertContext";
import { useApplyApplicationMutation, useGetApplicationByIdQuery } from "../../../services/applicationService";
import { application_weather, getProductUOMByName } from "../../../constants/enums";
import LoadingComponent from "../../../components/LoadingComponent";
import ErrorComponent from "../../../components/ErrorComponent";
import SuccessButton from "../../../components/SuccessButton";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import { dataGridStyles, localeText } from "../../../utils/dataGridConfig";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CancelButton from "../../../components/CancelButton";
import dayjs from 'dayjs'; // Import dayjs
import { formatDate } from "../../../utils/formatDate";
import { useSpendApplicationMutation } from "../../../services/inventoryService";

const ApplyApplication = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const user = useSelector((state) => state.auth.user);

  const { data: applicationDetails, isLoading, error, refetch } = useGetApplicationByIdQuery(id);

  const [updateApplication] = useApplyApplicationMutation();
  const [spend] = useSpendApplicationMutation();
  const [boomCapacity, setBoomCapacity] = useState(2000);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const dosagesWithIds = applicationDetails?.dosages?.map((dosage, index) => ({
    ...dosage,
    id: index,
  })) || [];

  const columns = [
    {
      field: "product_name",
      headerName: "Producto",
      flex: 1,
    },
    {
      field: "dosage_per_boom",
      headerName: "Cantidad por Boom",
      flex: 1,
      renderCell: (params) => {
        const dosageQuantity = params.row?.dosage_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${dosageQuantity * (boomCapacity / 2000)} ${uomLabel}`;
      },
    },
    {
      field: "total_quantity",
      headerName: "Cantidad Total",
      flex: 1,
      renderCell: (params) => {
        const totalQuantity = params.row?.total_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${totalQuantity} ${uomLabel}`;
      },
    },
  ];

  const handleFormSubmit = async (values, { resetForm }) => {
    const updatedApplication = {
      applied_date: values.appliedDate ? dayjs(values.appliedDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'), // Format as 'YYYY-MM-DD'
      operator: values.operator,
      wheather: values.weather
    };
    try {
      await updateApplication({ id, updatedApplication }).unwrap();
      await spend(id).unwrap();
      await refetch();
      resetForm();
      navigate("/applications");
      showAlert("Dosificación Aplicada!", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  const initialValues = {
    appliedDate: null,
    operator: user?.name || "",
    weather: 4,
  };

  const applicationSchema = yup.object().shape({
    operator: yup.string().required("Requerido"),
    weather: yup.number().required("Requerido"),
  });

  return (
    <Box m="20px">
      <Header title="Aplicar Dosificación" />

      <Formik onSubmit={handleFormSubmit} initialValues={initialValues} validationSchema={applicationSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px" gridTemplateColumns="repeat(14, minmax(0, 1fr))">
              <Box sx={{ gridColumn: "span 5" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip 
                    title="Fecha por defecto es el día actual" 
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '0.9rem',
                        },
                      },
                    }}
                  >
                      <InfoIcon sx={{ color: colors.greenAccent[500], cursor: "pointer"}} />
                    </Tooltip>
                    <DatePicker
                      label="Fecha aplicada"
                      value={values.appliedDate ? dayjs(values.appliedDate) : null} // Convert to Day.js object
                      onChange={(newValue) => setFieldValue("appliedDate", newValue)} // Set as Day.js object
                      format="DD/MM/YYYY" // Set user-friendly format
                      slotProps={{
                        textField: {
                          variant: "filled",
                          fullWidth: true,
                          error: !!touched.appliedDate && !!errors.appliedDate,
                          helperText: touched.appliedDate && errors.appliedDate
                        }
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Operario"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.operator}
                name="operator"
                error={!!touched.operator && !!errors.operator}
                helperText={touched.operator && errors.operator}
                sx={{ gridColumn: "span 4" }}
                disabled
              />
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 5" }}>
                <InputLabel>Clima</InputLabel>
                <Select id="weather" name="weather" value={values.weather} onChange={handleChange}>
                  {application_weather.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Box display="grid" gap="20px" gridTemplateColumns="repeat(7, minmax(0, 1fr))">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Nombre de Aplicación
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Fecha Programada
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {formatDate(applicationDetails.programmed_date)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Lote
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.lote_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Bloques a aplicar
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.location}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Cantidad de Booms
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.total_area * (2000 / boomCapacity)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
                  Hectáreas
                </Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                  {applicationDetails.total_area}
                </Typography>
              </Box>

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="boom-capacity-label" sx={{ color: colors.grey[100] }}>
                    Capacidad del Boom
                  </InputLabel>
                  <Select
                    labelId="boom-capacity-label"
                    value={boomCapacity}
                    label="Capacidad"
                    onChange={(e) => setBoomCapacity(e.target.value)}
                    sx={{
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      width: "120px",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                      },
                    }}
                  >
                    <MenuItem value={2000}>2000 L</MenuItem>
                    <MenuItem value={4000}>4000 L</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Typography variant="h5" fontWeight="bold" color={colors.greenAccent[500]}>
              Dosificación
            </Typography>

            <Box height="40vh" sx={dataGridStyles(colors)}>
              <DataGrid
                rows={dosagesWithIds}
                columns={columns}
                getRowId={(row) => row.id}
                pageSizeOptions={[100]}
                loadingText="Cargando..."
                localeText={localeText}
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnSorting
              />
            </Box>      
            <Box
              display="flex"
              justifyContent="end"
              mt="2vh"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/applications')}/>
              <SuccessButton label="Aplicar Dosificación" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ApplyApplication;